import { DestroyRef, Injectable, computed, inject, signal } from '@angular/core';
import { AuthTokenService } from '@core/auth/auth-token.service';
import { isCallerHandledError } from '@core/interceptors/http-error.interceptor';
import { AppError } from '@core/models/app-error.model';
import { NotificationService } from '@core/notifications/notification.service';
import { Subscription, defer, firstValueFrom, repeat, retry, takeUntil, takeWhile, timer } from 'rxjs';
import { ProgramQuery } from './models/program-configuration.model';
import { TransferStatus } from './models/transfer.model';
import { TransferService } from './transfer.service';

export interface TrackedTransfer {
  id: string;
  program: ProgramQuery;
  label: string;
  status: TransferStatus;
  total: number;
  processed: number;
  errorMessage: string | null;
  startedAt: number;
  finishedAt: number | null;
  trackingIssue: 'unreachable' | 'timeout' | null;
}

interface StoredTransfer {
  id: string;
  program: ProgramQuery;
  label: string;
  startedAt: number;
}

interface StoredTransfers {
  username: string | null;
  transfers: StoredTransfer[];
}

const STORAGE_KEY = 'incentivos.transfers';
const POLL_DELAYS_MS = [2000, 2000, 3000, 3000, 5000];
const MAX_POLL_DELAY_MS = 8000;
const POLL_RETRIES = 2;
const MAX_TRACKING_MS = 10 * 60 * 1000;

const FINAL_STATUSES: ReadonlySet<TransferStatus> = new Set(['completed', 'error']);

function isAppError(error: unknown): error is AppError {
  return typeof error === 'object' && error !== null && Array.isArray((error as AppError).messages);
}

export function isTransferActive(transfer: TrackedTransfer): boolean {
  return !FINAL_STATUSES.has(transfer.status) && !transfer.trackingIssue;
}

function pollDelay(attempt: number): number {
  return POLL_DELAYS_MS[attempt - 1] ?? MAX_POLL_DELAY_MS;
}


@Injectable()
export class TransferTracker {
  private readonly transferService = inject(TransferService);
  private readonly notificationService = inject(NotificationService);
  private readonly authTokenService = inject(AuthTokenService);

  private readonly polls = new Map<string, Subscription>();
  private readonly state = signal<readonly TrackedTransfer[]>([]);

  readonly transfers = this.state.asReadonly();
  readonly activeCount = computed(() => this.state().filter(isTransferActive).length);

  constructor() {
    inject(DestroyRef).onDestroy(() => this.polls.forEach((poll) => poll.unsubscribe()));
    this.restore();
  }

  transferFor(incentiveId: number): TrackedTransfer | undefined {
    return [...this.state()].reverse().find((transfer) => transfer.program.incentiveId === incentiveId);
  }

  async send(program: ProgramQuery, label: string): Promise<void> {
    const current = this.transferFor(program.incentiveId);
    if (current && current.status !== 'error') return;

    const id = await firstValueFrom(this.transferService.createTransfer(program));
    if (current) this.remove(current.id);

    this.add({ id, program, label, startedAt: Date.now() });
  }

  async retry(id: string): Promise<void> {
    const transfer = this.find(id);
    if (!transfer) return;

    if (transfer.status === 'error') {
      try {
        await this.send(transfer.program, transfer.label);
      } catch (error) {
        // HTTP errors outside 401-500 were already toasted by the interceptor.
        if (isAppError(error) && (error.statusCode === 200 || isCallerHandledError(error))) {
          this.notificationService.error(error.messages.join(' '), { title: 'No fue posible reenviar a validación técnica' });
        }
      }
      return;
    }

    this.patch(id, { trackingIssue: null });
    this.poll(id);
  }

  dismiss(id: string): void {
    const transfer = this.find(id);
    if (transfer && !isTransferActive(transfer)) this.remove(id);
  }

  private add(stored: StoredTransfer): void {
    this.state.update((list) => [
      ...list,
      { ...stored, status: 'pending', total: 0, processed: 0, errorMessage: null, finishedAt: null, trackingIssue: null },
    ]);
    this.persist();
    this.poll(stored.id);
  }

  private poll(id: string): void {
    this.polls.get(id)?.unsubscribe();

    const subscription = defer(() => this.transferService.getTransfer(id))
      .pipe(
        retry({ count: POLL_RETRIES, delay: (_, attempt) => timer(attempt * 2000) }),
        repeat({ delay: (count) => timer(pollDelay(count)) }),
        takeWhile((progress) => !FINAL_STATUSES.has(progress.status), true),
        takeUntil(timer(MAX_TRACKING_MS)),
      )
      .subscribe({
        next: (progress) => {
          const finished = FINAL_STATUSES.has(progress.status);
          this.patch(id, {
            status: progress.status,
            total: progress.total,
            processed: progress.processed,
            errorMessage: progress.errorMessage,
            finishedAt: finished ? Date.now() : null,
          });
          if (finished) this.onFinished(id);
        },
        error: () => this.stopTracking(id, 'unreachable'),
        complete: () => {
          const transfer = this.find(id);
          if (transfer && !FINAL_STATUSES.has(transfer.status)) this.stopTracking(id, 'timeout');
        },
      });

    this.polls.set(id, subscription);
  }

  private onFinished(id: string): void {
    this.polls.delete(id);
    this.persist();

    const transfer = this.find(id);
    if (!transfer) return;

    if (transfer.status === 'completed') {
      this.notificationService.success(`${transfer.label} se envió a validación técnica.`);
    } else {
      this.notificationService.error(transfer.errorMessage ?? 'La transferencia terminó con error.', {
        title: 'No se completó el envío a validación técnica',
      });
    }
  }

  private stopTracking(id: string, issue: TrackedTransfer['trackingIssue']): void {
    this.polls.delete(id);
    this.patch(id, { trackingIssue: issue });
  }

  private find(id: string): TrackedTransfer | undefined {
    return this.state().find((transfer) => transfer.id === id);
  }

  private patch(id: string, changes: Partial<TrackedTransfer>): void {
    this.state.update((list) => list.map((transfer) => (transfer.id === id ? { ...transfer, ...changes } : transfer)));
  }

  private remove(id: string): void {
    this.polls.get(id)?.unsubscribe();
    this.polls.delete(id);
    this.state.update((list) => list.filter((transfer) => transfer.id !== id));
    this.persist();
  }

  private persist(): void {
    const transfers = this.state()
      .filter((transfer) => !FINAL_STATUSES.has(transfer.status))
      .map(({ id, program, label, startedAt }) => ({ id, program, label, startedAt }));

    try {
      if (transfers.length) {
        const stored: StoredTransfers = { username: this.authTokenService.username(), transfers };
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } else {
        sessionStorage.removeItem(STORAGE_KEY);
      }
    } catch {
      // Storage may be unavailable (private mode, quota); tracking still works until the page reloads.
    }
  }

  private restore(): void {
    let stored: StoredTransfers | null = null;
    try {
      stored = JSON.parse(sessionStorage.getItem(STORAGE_KEY) ?? 'null') as StoredTransfers | null;
    } catch {
      stored = null;
    }

    if (!stored || stored.username !== this.authTokenService.username() || !Array.isArray(stored.transfers)) return;
    stored.transfers.forEach((transfer) => this.add(transfer));
  }
}
